import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import CameraAltIcon from "@mui/icons-material/CameraAltOutlined";
import PersonIcon from "@mui/icons-material/PersonOutline";
import { s3baseUri } from "../../../config/config";
import GeneralMuiModal from "../../menu/MenuModalMui";
import FixedSizeImageCropper from "../../FixedSizeImageCropper/FixedSizeImageCropper";
import { enqueueSnackbar } from "notistack";
import { get_types_list } from "../../../DAL/features/features";
import GeneralCkeditor from "../../editors/GeneralCkEditor";

export default function ClientProfileFields({
  inputs,
  croppedImage,
  setCroppedImage,
  setInputs,
  isEditForm = true,
  isCompanyProfile = false,
  isAllowSectorFlow = false,
  handleSectorChange = () => {},
  sectors = {},
  isTypeAndShortSummaryAllow = false,
  feature_slug = "",
  isCoachFields = false,
  companyType = "",
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [limitError, setLimitError] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [typesList, setTypesList] = useState([]);

  const handleMenuOpen = (event) => {
    console.info("inputs open menu", event);
    setInputs((old) => ({
      ...old,
      anchorEl: event.target,
    }));
  };

  const handleMenuClose = () => {
    inputs.anchorEl?.blur();
    setInputs((old) => ({ ...old, anchorEl: null }));
  };

  const handleViewProfile = () => {
    setInputs((old) => ({ ...old, dialogOpen: true }));
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setInputs((old) => ({ ...old, dialogOpen: false }));
    handleMenuClose();
  };

  const handleUpdateImage = () => {
    fileInputRef.current.click();
    handleMenuClose();
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name == "short_summary") {
      if (value?.length > 150) {
        setLimitError("Maximum 150 characters allowed.");
        return;
      } else {
        setLimitError("");
      }
    }
    setInputs((old) => ({ ...old, [name]: value }));
  };

  const fileChangedHandler = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Handle SVG files differently
    if (file.type === "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = () => {
        // Directly set the SVG as preview and file
        setCroppedImage(reader.result);
        setInputs((prev) => ({ ...prev, image: file }));
      };
      reader.readAsDataURL(file);
      return; // Skip the cropping modal for SVG
    }

    // Original handling for other image types
    const reader = new FileReader();
    reader.onload = () => {
      setFile(reader.result);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleGetTypesAgainstFeatures = async () => {
    const resp = await get_types_list(feature_slug);
    if (resp.code == 200) {
      setTypesList(resp.data);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };
  useEffect(() => {
    if (isCompanyProfile || isTypeAndShortSummaryAllow) {
      handleGetTypesAgainstFeatures();
    }
  }, []);

  return (
    <>
      {cropModalOpen && (
        <>
          <FixedSizeImageCropper
            image={file}
            maxSize={{ width: 1080, height: 1080 }}
            onClose={() => setCropModalOpen(false)}
            aspectRatio={1 / 1}
            handleCropComplete={(file) => {
              setCroppedImage(URL.createObjectURL(file));
              setInputs((prev) => ({ ...prev, image: file }));
              setCropModalOpen(false);
            }}
            roundCropAreaPixels={true}
            open={cropModalOpen}
          />
        </>
      )}

      {inputs.dialogOpen && (
        <GeneralMuiModal
          open={inputs.dialogOpen}
          setOpen={handleDialogClose}
          componentToPass={
            <>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  className="de-flex text-center justify-cotent-center"
                  alt="Profile"
                  src={
                    croppedImage ||
                    (inputs.image && typeof inputs.image === "string"
                      ? s3baseUri + inputs.image
                      : "")
                  }
                  style={{ width: 200, height: 200, margin: "0 auto" }}
                />
              </div>
            </>
          }
        />
      )}

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Avatar
          alt="Profile Picture"
          src={
            croppedImage ||
            (inputs.image && typeof inputs.image === "string"
              ? s3baseUri + inputs.image
              : "")
          }
          sx={{ width: 100, height: 100, mb: 2 }}
        />

        <IconButton
          color="primary"
          aria-controls="profile-menu"
          aria-haspopup="true"
          onClick={handleMenuOpen}
          sx={{
            backgroundColor: "white",
            color: "navy",
            borderRadius: "50%",
            bottom: "50px",
            left: "40px",
            "&:hover": {
              color: "navy",
              backgroundColor: "white",
            },
            "&:focus": {
              color: "navy",
              backgroundColor: "white !important",
            },
          }}
        >
          <CameraAltIcon />
        </IconButton>

        <Menu
          id="profile-menu"
          aria-controls="profile-menu"
          aria-haspopup="true"
          anchorEl={inputs.anchorEl}
          open={Boolean(inputs.anchorEl)}
          onClose={handleMenuClose}
          disableRestoreFocus
          disableAutoFocusItem
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          {!isCompanyProfile && (
            <MenuItem onClick={handleUpdateImage}>
              <ListItemIcon>
                <CameraAltIcon fontSize="small" />
              </ListItemIcon>
              {croppedImage || inputs.image ? "Update Image" : "Upload Image"}
            </MenuItem>
          )}
          {(croppedImage || inputs.image) && (
            <MenuItem onClick={handleViewProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              View Image
            </MenuItem>
          )}
        </Menu>

        <input
          hidden
          accept="image/jpeg, image/jpg, image/png, image/webp"
          type="file"
          ref={fileInputRef}
          onChange={fileChangedHandler}
        />

        {/* Crop Modal */}
        {/* <CropPopUp
                                cropModalOpen={cropModalOpen}
                                handleModalClose={() => setCropModalOpen(false)}
                                file={file}
                                crop={crop}
                                setCrop={setCrop}
                                onImageLoaded={onImageLoaded}
                                onCropComplete={onCropComplete}
                                handleCropImage={handleCropImage}
                                handleSaveFullImage={handleSaveFullImage}
                              /> */}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="contact_name"
              label={isCoachFields ? "Coach Name" : "Contact Name"}
              value={inputs.contact_name}
              onChange={handleChange}
              required
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="company_name"
              label="Company Name"
              value={inputs.company_name}
              onChange={handleChange}
              required
              disabled={isCompanyProfile || isCoachFields}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="contact_number"
              label="Contact Number"
              value={inputs.contact_number}
              onChange={handleChange}
              required={!isCoachFields}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>

          {!isCoachFields && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="post_code"
                label="Post Code"
                value={inputs.post_code}
                onChange={handleChange}
                required
                disabled={isCompanyProfile}
                InputProps={{
                  readOnly: isCompanyProfile,
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              name="email"
              label="Email"
              value={inputs.email}
              onChange={handleChange}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="location"
              label="Location"
              value={inputs.location}
              onChange={handleChange}
              disabled={isCompanyProfile}
              required
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required={!isCoachFields}
              name="website_url"
              label="Website"
              value={inputs.website_url}
              onChange={handleChange}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              // required
              fullWidth
              name="facebook_link"
              label="Facebook URL"
              value={inputs.facebook_link}
              onChange={handleChange}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              // required
              fullWidth
              name="linkedIn_link"
              label="LinkedIn URL"
              value={inputs.linkedIn_link}
              onChange={handleChange}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              // required
              fullWidth
              name="instagram_link"
              label="Instagram URL"
              value={inputs.instagram_link}
              onChange={handleChange}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              // required
              fullWidth
              name="youtube"
              label="YouTube URL"
              value={inputs.youtube}
              onChange={handleChange}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              // required
              fullWidth
              name="twitter_link"
              label="Twitter URL"
              value={inputs.twitter_link}
              onChange={handleChange}
              disabled={isCompanyProfile}
              InputProps={{
                readOnly: isCompanyProfile,
              }}
            />
          </Grid>
          {/* Dynamic Sector Fields based on Client Types */}
          {isAllowSectorFlow &&
            (() => {
              // Show sectors when we have data from API
              return inputs?.clientTypeHavingSectors?.map(
                (clientType, index) => {
                  // Get the original client type object to extract title for label

                  // Use title from original object, fallback to formatted clientType
                  const labelText = clientType.title;

                  return (
                    <Grid item xs={12} sm={6} key={clientType._id}>
                      <FormControl fullWidth required>
                        <InputLabel id={`sector-select-${clientType._id}`}>
                          {labelText} Sector
                        </InputLabel>
                        <Select
                          labelId={`sector-select-${clientType._id}`}
                          id={`sector-select-${clientType._id}`}
                          value={clientType.sector.slug}
                          label={`${labelText} Sector`}
                          disabled={isCompanyProfile}
                          onChange={(e) =>
                            handleSectorChange(clientType.slug, e.target.value)
                          }
                        >
                          {(() => {
                            const options = [];
                            // Add other sectors from API
                            if (sectors[clientType.slug]) {
                              sectors[clientType.slug].forEach((sector) => {
                                options.push(
                                  <MenuItem
                                    key={sector.slug}
                                    value={sector.slug}
                                  >
                                    {sector.title}
                                  </MenuItem>
                                );
                              });
                            }

                            return options;
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }
              );
            })()}

          {(isCompanyProfile || isTypeAndShortSummaryAllow) && (
            <>
              <Grid item xs={12} sm={6} key={"feature_type"}>
                <Autocomplete
                  multiple
                  options={typesList}
                  getOptionLabel={(option) => option.name}
                  value={
                    typesList.filter((t) =>
                      inputs.profile_type_id?.includes(t._id)
                    ) || []
                  }
                  onChange={(e, newValue) => {
                    handleChange({
                      target: {
                        name: "profile_type_id",
                        value: newValue.map((v) => v._id),
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <>
                      <TextField {...params} label="Type *" />
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12} mb={0}>
                <TextField
                  multiline
                  fullWidth
                  required
                  rows={3}
                  label="Short Summary"
                  onChange={handleChange}
                  name="short_summary"
                  value={inputs.short_summary}
                  error={Boolean(limitError)}
                  helperText={
                    limitError ||
                    `${inputs?.short_summary?.length || 0}/150 characters`
                  }
                />
              </Grid>
            </>
          )}

          {!isCoachFields && (
            <Grid item xs={12} mb={0}>
              <TextField
                fullWidth
                margin="normal"
                id="tagline"
                label="Tagline"
                name="tagline"
                multiline
                rows={3}
                disabled={isCompanyProfile}
                value={inputs.tagline}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 121) {
                    handleChange(e);
                  }
                }}
                error={inputs.tagline?.length > 120}
                helperText={
                  inputs.tagline?.length > 120
                    ? "Tagline cannot exceed 120 characters"
                    : `${inputs.tagline?.length || 0}/120 characters`
                }
                inputProps={{ maxLength: 121 }} // allow 1 extra to trigger error UI
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "6px",
                  },
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} mb={0}>
            <Typography
              variant="h6"
              style={{ marginBottom: 0, fontSize: "16px " }}
            >
              {isCoachFields ? "Coach Description *" : "About Yourself *"}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            mt={0}
            sx={{ paddingBlockStart: "8px !important" }}
          >
            {isCompanyProfile ? (
              <div className="card shadow-md p-3 mb-2 about-content">
                <p
                  className="ps-2 ckeditor tinymce-reset"
                  dangerouslySetInnerHTML={{
                    __html: inputs.about,
                  }}
                ></p>
              </div>
            ) : (
              <>
                {/* <TextEditor
                                required
                                inputs={inputs}
                                setInputs={setInputs}
                                name="about"
                                isLimited={true}
                                type="about"
                              /> */}
                <GeneralCkeditor
                  inputs={inputs}
                  setInputs={setInputs}
                  name="about"
                />
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
